/**
 * BrowserCompatBanner — 149-cw-r142 (2026-08-31).
 *
 * Banner ringan (amber, tinggi kecil, dismissible) yang muncul ketika browser
 * user TIDAK memenuhi requirement modern feature yang kita pakai:
 *   - `CSS.supports('overflow: clip')` — dipakai wrapper tabel sticky
 *   - `CSS.supports('selector(:has(*))')` — Tailwind arbitrary + CSS `:has()` selectors
 *
 * Kalau salah satu feature tidak ada, kemungkinan user pakai browser lama
 * (Safari <16, Chrome <105, Firefox <121). Kita tampilkan banner sekali saja
 * (dismiss tersimpan di localStorage, versi banner disemat supaya kalau ada
 * requirement baru, banner akan muncul lagi).
 *
 * Non-blocking: banner ringan (tinggi 32px, amber-100), tombol X untuk dismiss.
 * Tidak mengganggu layout.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

const BANNER_VERSION = "r142";
const STORAGE_KEY = `ens_browser_compat_dismissed_${BANNER_VERSION}`;

function _detect() {
  // Return { supported: bool, missing: string[] }
  if (typeof window === "undefined" || !window.CSS || !CSS.supports) {
    // Sangat lama — CSS.supports diperkenalkan sejak Chrome 28/Firefox 22/Safari 9.
    return { supported: false, missing: ["CSS.supports"] };
  }
  const missing = [];
  if (!CSS.supports("overflow", "clip")) missing.push("overflow: clip (tabel sticky)");
  try {
    if (!CSS.supports("selector(:has(*))")) missing.push(":has() selector (layout dinamis)");
  } catch { /* Safari 15 melempar error di CSS.supports selector() — anggap missing. */
    missing.push(":has() selector (layout dinamis)");
  }
  return { supported: missing.length === 0, missing };
}

function _browserGuess() {
  if (typeof navigator === "undefined") return "browser Anda";
  const ua = navigator.userAgent || "";
  if (/Edg\//.test(ua)) return "Microsoft Edge";
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return "Google Chrome";
  if (/Firefox\//.test(ua)) return "Mozilla Firefox";
  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return "Safari (Apple)";
  return "browser Anda";
}

export default function BrowserCompatBanner() {
  const [dismissed, setDismissed] = useState(true);
  const [info, setInfo] = useState({ supported: true, missing: [] });

  useEffect(() => {
    const already = typeof localStorage !== "undefined" && localStorage.getItem(STORAGE_KEY) === "1";
    const det = _detect();
    setInfo(det);
    setDismissed(already || det.supported);
  }, []);

  if (dismissed || info.supported) return null;

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
    setDismissed(true);
  };

  const browser = _browserGuess();

  return (
    <div
      data-testid="browser-compat-banner"
      className="flex items-center gap-3 bg-amber-50 border-b border-amber-300 px-4 py-1.5 text-[11px] text-amber-900"
      role="alert"
    >
      <AlertTriangle size={13} className="text-amber-700 shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-semibold">Browser {browser} Anda perlu diperbarui.</span>{" "}
        <span className="text-amber-800">
          Beberapa tampilan (tabel sticky, layout dinamis) mungkin salah render. Update ke Safari 16+, Chrome 105+, Firefox 121+, atau Edge 105+ untuk pengalaman optimal.
        </span>
      </div>
      <button
        type="button"
        onClick={dismiss}
        data-testid="browser-compat-dismiss"
        className="text-amber-700 hover:text-amber-900 shrink-0"
        aria-label="Tutup peringatan"
        title="Sembunyikan peringatan ini"
      >
        <X size={13} />
      </button>
    </div>
  );
}
